import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Budget } from '@bingads-webui-campaign/campaign-ui-schemas';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Form } from '@bingads-webui-react/primitive';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { getBudgetEditorStyle } from '../styles';
import { BaseEditor, KEYSENTER } from './base-editor';
import { getBulkEditResponseErrors } from '../helpers';
import { BudgetAmountEditor } from './budget-base-component';
import { BudgetEditorWithLandscape } from './campaign-budget/budget-landscape';

const budgetSchema = {
  ...Budget,
  properties: {
    ...Budget.properties,
    Amount: {
      ...Budget.properties.Amount,
      nullable: false,
      type: 'number',
      maxLength: 15,
    },
  },
};

class SharedBudgetEditorInternal extends React.Component {
  static propTypes = {
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }).isRequired,
    data: PropTypes.shape({
      Amount: PropTypes.number,
    }).isRequired,
    scope: PropTypes.instanceOf(QueryParam).isRequired,
    classes: PropTypes.shape({}).isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    renderCustomContent: PropTypes.func,
    updateSharedBudget: PropTypes.func.isRequired,
    summaryBarActivityLogger: PropTypes.shape({
      summaryBarBudgetActivity: PropTypes.any,
    }).isRequired,
    helpId: PropTypes.isRequired,
    showBudgetLandscape: PropTypes.bool,
    budgetId: PropTypes.isRequired,
  };

  static defaultProps = {
    renderCustomContent: null,
    showBudgetLandscape: false,
  };

  constructor(props) {
    super(props);

    this.activityLogger = props.summaryBarActivityLogger
      && props.summaryBarActivityLogger.summaryBarBudgetActivity.create();
  }

  state = {
    isValid: false,
    data: this.props.data,
    serverErrors: [],
  }

  onValidate = (result) => {
    if (result.data.Amount !== this.state.data.Amount) {
      this.setState({
        isValid: result.isValid,
        data: { Amount: result.data.Amount },
      });
    }
  }

  onKeyDown = (e) => {
    if (e && e.which === KEYSENTER) {
      e.preventDefault();
      e.stopPropagation();
      this.onSave();
    }
  }

  onSave = () => {
    if (this.state.isValid) {
      const updateSharedBudget = (hasBudgetLandscape = false) => this.props.updateSharedBudget(this.state.data)
        .then((response) => {
          this.props.onSave(response);
          if (this.activityLogger) {
            this.activityLogger.trace(
              {
                updateStatus: 'successful',
                value: this.state.data,
                component: 'campaign summary bar',
                budgetType: 'inline shared budget',
                hasBudgetLandscape,
                ...(hasBudgetLandscape ? {
                  ActionName: 'Accept',
                  budgetId: this.props.budgetId,
                  Channel: 'ClientName:bingadsweb[SummaryBarEditing]',
                  campaignId: this.tableView.campaignId,
                  opportunityId: this.tableView.opportunityId,
                  suggestedBudget: this.tableView.ko_model.rawSuggestedBudget,
                  currency: this.tableView.i18n.currency,
                } : {}),
              },
              'AdInsight.Budget'
            );
          }
        })
        .catch((response) => {
          const serverErrors = getBulkEditResponseErrors(response, this.props.i18n);
          this.setState({ serverErrors });

          // server error is reported to user trace here as well
          if (this.activityLogger) {
            this.activityLogger.trace({
              updateStatus: 'failed',
              serverErrors,
              component: 'campaign summary bar',
              budgetType: 'inline shared budget',
              hasBudgetLandscape,
            });
          }
        });

      if (this.tableView) {
        if (this.tableView.getSelectedNewBudget() === '') {
          // User did nothing on on the table.
          this.props.onCancel();
        } else {
          this.setState({ data: { ...this.state.data, Amount: this.tableView.getSelectedNewBudget() } }, () => updateSharedBudget(true));
        }
      } else {
        updateSharedBudget();
      }
    }
  }

  setTableView = (tableView) => {
    if (tableView) {
      this.tableView = tableView;
      this.tableView.on('saveCustomValueWithKeyboard', () => {
        this.onSave();
      });
    }
  }

  render() {
    const {
      i18n,
      classes,
      onCancel,
      renderCustomContent,
      helpId,
      showBudgetLandscape,
      scope,
      budgetId,
    } = this.props;
    const {
      data,
      serverErrors,
      isValid,
    } = this.state;

    return (
      <BaseEditor onSave={this.onSave} onCancel={onCancel} isValid={isValid} isLoading={false}>
        <ValidationGroup validationProps={{
          schema: budgetSchema,
          onValidate: this.onValidate,
          data,
        }}
        >
          <Form className={classes.form}>{
            showBudgetLandscape ? (
              <div className="budget-editor-with-landscape form-group">
                <BudgetEditorWithLandscape
                  budgetAmount={data.Amount}
                  setTableView={this.setTableView}
                  activityLogger={this.activityLogger}
                  scope={scope}
                  i18n={i18n}
                  isSharedBudget
                  budgetId={budgetId}
                />
              </div>) : (
                <BudgetAmountEditor i18n={i18n} inputClassName="shared-budget-edit-budget-input" onKeyDown={this.onKeyDown} helpId={helpId} />
             )}
            {renderCustomContent && renderCustomContent()}
            <ErrorMessages messages={_.pluck(serverErrors, 'message')} />
          </Form>
        </ValidationGroup>
      </BaseEditor>
    );
  }
}

const wrappers = [
  withI18nConsumer,
  _.partial(withDefaultStyles, _, getBudgetEditorStyle),
];

const Wrapped = _.compose(...wrappers)(SharedBudgetEditorInternal);

export { Wrapped as SharedBudgetEditor };
